import { startCase } from 'lodash-es'

import type {
  MeasurementUnit,
  MeasurementUnitType,
  UnitRelation
} from '@/domains/measurement-units/types/models/MeasurementUnit.types'
import type { SelectOptionGroup, SelectOptionItem } from '@/types/common/models/SelectOption.types'

import {
  COUNTING_MEASUREMENT_TYPES,
  MEASUREMENT_TYPE_ORDER
} from '@/domains/measurement-units/types/consts/MeasurementType.consts'
import { ITEM_UNIT_IDENTIFIER } from '@/domains/measurement-units/types/consts/MeasurementUnitIdentifier.consts'
import { canHoldEveryBaseUnit, unitLabel } from '@/utils/unit-util/unitUtil'

/**
 * Map a MeasurementUnit to a SelectOptionItem.
 *
 * The value is the identifier, not the row key. A selector writes it straight into
 * `measurement_unit_identifier`, which every schema types as a string.
 */
export const measurementUnitToOption = (unit: MeasurementUnit): SelectOptionItem => ({
  value: unit.unit_identifier,
  label: unit.name,
  raw: unit
})

/** Find a measurement unit by its identifier within an array */
export const findMeasurementUnitByIdentifier = (
  units: MeasurementUnit[],
  unitIdentifier: string
): MeasurementUnit | undefined => units.find(unit => unit.unit_identifier === unitIdentifier)

/**
 * The unit a document line starts in when a product is picked: the one the product is priced in,
 * so a cheese priced per kilogram is ordered, billed and invoiced in kilograms. A product with no
 * pricing unit is sold per item.
 *
 * Every pick sets it, including a swap to another product, because the old product's unit says
 * nothing about the new one.
 */
export const pricingUnitIdentifierOf = (product: { pricing_unit?: UnitRelation }): string =>
  product.pricing_unit?.unit_identifier ?? ITEM_UNIT_IDENTIFIER

/**
 * The base unit of `unit`'s type — grams for kilograms, millilitres for litres, items for items.
 *
 * Stock is a whole count of this unit, so a stock figure reads in it. The type names its base
 * row through `base_unit_id`; a type without one falls back to the unit flagged `is_base_unit`.
 */
export const findBaseUnit = (units: MeasurementUnit[], unit: MeasurementUnit): MeasurementUnit | undefined =>
  units.find(candidate => String(candidate.id) === unit.type.base_unit_id) ??
  units.find(candidate => candidate.is_base_unit && candidate.type_id === unit.type_id)

/**
 * The base unit a stock figure reads in, for a wire relation — a variant's `pricing_unit`. The
 * relation carries no type, so it is resolved through the catalogue first. Undefined when it names
 * no unit the catalogue holds.
 */
export const baseUnitOfRelation = (units: MeasurementUnit[], relation?: UnitRelation): MeasurementUnit | undefined => {
  const resolved = relation?.unit_identifier
    ? findMeasurementUnitByIdentifier(units, relation.unit_identifier)
    : undefined

  return resolved ? findBaseUnit(units, resolved) : undefined
}

/**
 * A unit a converting picker may offer: activated, and with a factor. A
 * packaging unit (null factor) never converts, so it is excluded — a line saved
 * in one fails on the backend.
 */
export const isPickableUnit = (unit: MeasurementUnit): unit is MeasurementUnit & { conversion_factor: number } =>
  unit.is_activated && unit.conversion_factor !== null

/** A unit that counts things rather than measuring them, and so agrees with a number. */
export const isCountingUnit = (unit: MeasurementUnit): boolean => COUNTING_MEASUREMENT_TYPES.includes(unit.type_id)

/**
 * The unit a variant's stock reads and is typed in: its pricing unit when that unit holds every base
 * unit exactly, else the base unit of its type. Undefined when the relation names no unit the catalogue holds.
 */
export const stockUnitOfRelation = (units: MeasurementUnit[], relation?: UnitRelation): MeasurementUnit | undefined => {
  const pricingUnit = relation?.unit_identifier
    ? findMeasurementUnitByIdentifier(units, relation.unit_identifier)
    : undefined

  if (pricingUnit === undefined) return undefined

  return canHoldEveryBaseUnit({ unit: pricingUnit }) ? pricingUnit : findBaseUnit(units, pricingUnit)
}

/**
 * The units a stock input may be typed in: activated, of the stock unit's type, and convertible exactly.
 * A count offers none, because a count has no unit to pick.
 */
export const stockUnitOptionsFor = (units: MeasurementUnit[], relation?: UnitRelation): MeasurementUnit[] => {
  const stockUnit = stockUnitOfRelation(units, relation)

  if (stockUnit === undefined || isCountingUnit(stockUnit)) return []

  return units
    .filter(isPickableUnit)
    .filter(unit => unit.type_id === stockUnit.type_id)
    .filter(unit => canHoldEveryBaseUnit({ unit }))
    .sort((a, b) => a.conversion_factor - b.conversion_factor)
}

/**
 * The units a purchase line for this variant may be entered in: the stock unit's options, or the count alone.
 *
 * ⚠ Narrower than the catalogue on purpose. A purchase order refuses a line in a unit of another type, and a
 * bill posts no stock for one, so offering kilograms on a counted product only leads to one of those.
 */
export const lineUnitOptionsFor = (units: MeasurementUnit[], relation?: UnitRelation): MeasurementUnit[] => {
  const stockUnit = stockUnitOfRelation(units, relation)

  if (stockUnit === undefined) return []

  return isCountingUnit(stockUnit) ? [stockUnit] : stockUnitOptionsFor(units, relation)
}

/**
 * What a unit reads as beside a quantity — `kg`, `ml`, `6 items`, `1 item`. `unitUtil.unitLabel`
 * owns the rule.
 *
 * The identifier is the last fallback rather than an error: a unit a merchant has since
 * deleted still reads as something rather than as a bare number, and `2` alone is unsafe when
 * 2 kg and 2 g are the same two characters.
 */
export const measurementUnitLabel = (unit: MeasurementUnit | undefined, quantity: number, fallback: string): string =>
  unit === undefined ? fallback : unitLabel({ unit, quantity })

/**
 * What a measurement type reads as — a group header in a picker, a parent row in
 * the management table.
 *
 * ⚠ `measurement_unit_types` has **no `name` column**. The create migration never added
 * one, and `MeasurementUnitSeeder` writes only `type`. So `name` is null for every type
 * today, and the slug is the label in practice. The `??` is there for the column if it
 * ever lands.
 */
export const measurementTypeLabel = (type: MeasurementUnitType): string => type.name ?? startCase(type.unit_identifier)

/**
 * Where a type sits among the group headers. One absent from `MEASUREMENT_TYPE_ORDER`
 * ranks after every named one.
 */
const typeRank = (typeIdentifier: string): number => {
  const rank = (MEASUREMENT_TYPE_ORDER as readonly string[]).indexOf(typeIdentifier)

  return rank === -1 ? MEASUREMENT_TYPE_ORDER.length : rank
}

/**
 * Smallest unit first. `conversion_factor` is how many base units one of this unit
 * holds, so ascending climbs the scale: millilitres above litres, grams above
 * kilograms.
 *
 * A null factor never converts, so it sorts last — `isPickableUnit` already keeps
 * those out of every picker. Equal factors fall back to the name, so the order never
 * depends on what the database happened to return first.
 */
const bySmallestFirst = (a: MeasurementUnit, b: MeasurementUnit): number =>
  (a.conversion_factor ?? Infinity) - (b.conversion_factor ?? Infinity) || a.name.localeCompare(b.name)

/** A group is never empty, so `[0]` names the type every unit in it shares. */
const byTypeThenLabel = (a: MeasurementUnit[], b: MeasurementUnit[]): number =>
  typeRank(a[0].type_id) - typeRank(b[0].type_id) ||
  measurementTypeLabel(a[0].type).localeCompare(measurementTypeLabel(b[0].type))

/**
 * Units grouped under their measurement type, smallest unit first inside each group.
 *
 * A flat picker offers millilitres beside kilograms beside pallets, in whatever order the
 * catalogue returned. A merchant then reads the whole list to find one unit. Grouped, they
 * scan to the dimension first and then down the scale.
 *
 * `toOption` is the caller's, because the two unit contracts disagree on the value. A
 * `measurement_unit_identifier` column wants the identifier, which is the default. A
 * `unit_id` foreign key wants the row id.
 */
export const groupMeasurementUnitOptions = (
  units: MeasurementUnit[],
  toOption: (unit: MeasurementUnit) => SelectOptionItem = measurementUnitToOption
): SelectOptionGroup[] => {
  const unitsByType = new Map<string, MeasurementUnit[]>()

  for (const unit of units) {
    const group = unitsByType.get(unit.type_id)

    if (group) group.push(unit)
    else unitsByType.set(unit.type_id, [unit])
  }

  return [...unitsByType.values()].sort(byTypeThenLabel).map(group => ({
    label: measurementTypeLabel(group[0].type),
    items: [...group].sort(bySmallestFirst).map(toOption)
  }))
}
